/* eslint-disable import/prefer-default-export */
import cloneDeep from 'lodash.clonedeep';
import ipRegex from 'ip-regex';
import {
  FormTemplate,
  FormTemplateOption,
  FormTemplateSection,
} from 'data/template/template.types';
import { Network } from 'data/network/network.types';
import { DialedDigitExtractionServer } from 'data/dialedDigitExtractionServer/dialedDigitExtractionServer.types';
import { NETWORK_ID_NOT_FOUND } from 'routes/AccessFunction/AccessFunction.types';
import { NetworkId } from 'data/networkId/networkId.types';

export type ListenersIpAddressPortsType = {
  [key:string]: string[],
}

export function adaptTemplateVisibilityMode(
  template: FormTemplate | undefined,
  mode: 'create' | 'edit' | 'view' | 'copy',
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);

  if (mode === 'edit') {
    (clonedTemplate.main as FormTemplateSection).fields.msid.readOnly = true;
  } else if (mode === 'view') {
    setAllFieldsAsReadOnlyExcludingFields(clonedTemplate, []);
  }

  return clonedTemplate;
}

export function adaptTemplateOptions(
  template: FormTemplate | undefined,
  networkInterfaceList: Network[],
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);
  (clonedTemplate.main as FormTemplateSection).fields.ownnetid.options = getNetworkOptions(networkInterfaceList, 'default');

  return clonedTemplate;
}

export const adaptGridData = (
  data: DialedDigitExtractionServer[],
  networks: NetworkId[],
): DialedDigitExtractionServer[] => {
  const clonedData = cloneDeep(data);
  return clonedData.map((dde) => {
    return { ...dde, ownnetid: getNetworkNameValue(networks, dde.ownnetid) };
  });
};

const getNetworkNameValue = (
  networks: NetworkId[],
  netId: string,
): string => {
  const networkId = networks.find(({ id }) => id === netId);
  let networkIdValue = '';
  if (networkId) {
    networkIdValue = `${networkId.networkId} - ${networkId.ownIPAddress}`;
  }

  if (networkId == null) {
    networkIdValue = NETWORK_ID_NOT_FOUND;
  }

  return networkIdValue;
};

export function adaptNetworkIdField(
  templateSection: FormTemplateSection,
  interfaceFormData: Partial<DialedDigitExtractionServer>,
): FormTemplateSection {

  let adaptedTemplate = templateSection;

  if (interfaceFormData.ownnetid != null) {
    adaptedTemplate = adaptNetworkIdFieldByFieldName(
      adaptedTemplate,
      interfaceFormData.ownnetid,
      'ownnetid',
    );
  }

  return adaptedTemplate;
}

function setAllFieldsAsReadOnlyExcludingFields(template: FormTemplate, excludingFields: string[]) {
  Object.entries(template).forEach(([_templateKey, templateEntry]) => {
    if (typeof templateEntry === 'string') {
      return;
    }
    Object.entries(templateEntry.fields).forEach(([fieldKey, fieldEntry]) => {
      if (!excludingFields.includes(fieldKey)) {
        fieldEntry.readOnly = true;
      }
    });
  });
}

function getNetworkOptions(
  networks: Network[],
  type: 'networkIdAndIpAddress' | 'networkIdIPAddressAndCustomPort' | 'networkIdIPAddressAndPortIncrement' | 'default',
): FormTemplateOption[] {
  return networks.map((network) => {
    const name = network.networkId;

    let ip = network.ownIPAddress;
    if (ipRegex.v6({ exact: true }).test(ip)) {
      ip = `[${ip}]`;
    }

    const port = network.startPortNumber;

    let label = '';
    switch (type) {
      case 'networkIdAndIpAddress':
        label = `${name} - ${ip}`;
        break;

      case 'networkIdIPAddressAndCustomPort':
        label = `${name} - ${ip}:{{value}}`;
        break;

      case 'networkIdIPAddressAndPortIncrement':
        label = `${name} - ${ip}:<% print(+${port} + (+value)); %>`;
        break;

      default:
        label = `${name} - ${ip}:${port}`;
    }

    const value = network.id;

    return {
      label,
      value,
    };
  });
}

function adaptNetworkIdFieldByFieldName(
  templateSection: FormTemplateSection,
  networkId: string,
  fieldName: 'ownnetid',
): FormTemplateSection {
  const options = templateSection.fields[fieldName]?.options;
  if (options == null || networkId === '') {
    return templateSection;
  }

  const netIdOption = options.find(({ value }) => value === networkId);
  if (netIdOption != null) {
    return templateSection;
  }

  const clonedTemplateSection = cloneDeep(templateSection);

  clonedTemplateSection.fields[fieldName].options?.push({
    label: networkId,
    value: networkId,
  });

  return clonedTemplateSection;
}
